'use client';

import { createContext, ReactNode, useState, useEffect, useCallback, useMemo } from 'react';
import type { User } from '@/lib/auth/types';

export interface AuthState {
    user: User | null;
    crewId: string | null;
    loading: boolean;
    /** Whether the server has AUTH_ENABLED=true. When false, no login wall. */
    authEnabled: boolean;
    /** Whether the current session is an admin impersonating another user */
    impersonating: boolean;
}

export interface AuthContextType extends AuthState {
    login: (_credentials: { email?: string; phone?: string; password?: string; pin?: string }) => Promise<{ user: User; crewId: string | null } | { error: string }>;
    logout: () => Promise<void>;
    refresh: () => Promise<void>;
    exitImpersonation: () => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | null>(null);

export function SessionProvider({ children }: { children: ReactNode }) {
    const [user, setUser] = useState<User | null>(null);
    const [crewId, setCrewId] = useState<string | null>(null);
    const [loading, setLoading] = useState(true);
    const [authEnabled, setAuthEnabled] = useState(false);
    const [impersonating, setImpersonating] = useState(false);

    const fetchMe = useCallback(async () => {
        try {
            const res = await fetch('/api/auth/me', { credentials: 'include' });
            if (!res.ok) {
                setUser(null);
                setCrewId(null);
                return;
            }
            const data = await res.json();
            setUser(data.user || null);
            setCrewId(data.crew_id || null);
            setAuthEnabled(data.auth_enabled ?? false);
            setImpersonating(data.impersonating ?? false);
        } catch {
            setUser(null);
            setCrewId(null);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchMe();
    }, [fetchMe]);

    const login = useCallback(async (credentials: { email?: string; phone?: string; password?: string; pin?: string }) => {
        const res = await fetch('/api/auth/login', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(credentials),
            credentials: 'include',
        });
        const data = await res.json();
        if (!res.ok) return { error: data.error || 'Login failed' };

        setUser(data.user);
        setCrewId(data.crew_id || null);
        return { user: data.user, crewId: data.crew_id || null };
    }, []);

    const logout = useCallback(async () => {
        await fetch('/api/auth/logout', { method: 'POST', credentials: 'include' });
        setUser(null);
        setCrewId(null);
        setImpersonating(false);
    }, []);

    const exitImpersonation = useCallback(async () => {
        await fetch('/api/admin/impersonate/exit', { method: 'POST', credentials: 'include' });
        await fetchMe();
        // A full navigation resets the authenticated session boundary and Next cache.
        // eslint-disable-next-line @next/next/no-location-assign-relative-destination -- impersonation exit must hard-navigate after session state changes.
        window.location.href = '/admin';
    }, [fetchMe]);

    const value = useMemo<AuthContextType>(() => ({
        user, crewId, loading, authEnabled, impersonating,
        login, logout, refresh: fetchMe, exitImpersonation,
    }), [user, crewId, loading, authEnabled, impersonating, login, logout, fetchMe, exitImpersonation]);

    return (
        <AuthContext.Provider value={value}>
            {children}
        </AuthContext.Provider>
    );
}
